import {
  REDO_RETURN_CARRIER_PICKER_GROUPS,
  registeredCarriersInPickerGroup,
} from "./redo-return-carrier-registry";

export interface ReturnCarrierPickerGroup {
  label: string;
  /** rate.carrier values this checkbox controls; the first is canonical. */
  carriers: readonly [string, ...string[]];
}

/** Merchant-facing picker groups derived from REDO_RETURN_CARRIER_PROVIDERS. */
export const returnSelectableCarrierGroups: readonly ReturnCarrierPickerGroup[] =
  REDO_RETURN_CARRIER_PICKER_GROUPS.flatMap((label) => {
    const [canonical, ...rest] = registeredCarriersInPickerGroup(label);
    if (!canonical) {
      return [];
    }
    return [{ label, carriers: [canonical, ...rest] }];
  });

const returnSelectableCarrierValues = new Set<string>(
  returnSelectableCarrierGroups.flatMap((group) => [...group.carriers]),
);

/** A carrier is governable per location exactly when some integration registered it. */
export function isReturnSelectableCarrier(carrier: string): boolean {
  return returnSelectableCarrierValues.has(carrier);
}

/** Brand label for a picker carrier; falls back to the raw rate.carrier string. */
export function getReturnSelectableCarrierLabel(carrier: string): string {
  return (
    returnSelectableCarrierGroups.find((group) =>
      group.carriers.includes(carrier),
    )?.label ?? carrier
  );
}

/** Every rate.carrier string a stored picker carrier governs; unknown carriers govern only themselves. */
export function getReturnSelectableCarrierGroupMembers(
  carrier: string,
): readonly string[] {
  return (
    returnSelectableCarrierGroups.find((group) =>
      group.carriers.includes(carrier),
    )?.carriers ?? [carrier]
  );
}

/** Labels of every group with a member in `carriers`; a partially stored group still counts as selected. */
export function getReturnSelectableCarrierGroupsFor(
  carriers: readonly string[],
): string[] {
  const present = new Set(carriers);
  return returnSelectableCarrierGroups
    .filter((group) => group.carriers.some((carrier) => present.has(carrier)))
    .map((group) => group.label);
}

/** Every rate.carrier string the named groups govern, in group order; unknown labels contribute nothing. */
export function getReturnSelectableCarriersForGroups(
  labels: readonly string[],
): string[] {
  const wanted = new Set(labels);
  return returnSelectableCarrierGroups
    .filter((group) => wanted.has(group.label))
    .flatMap((group) => [...group.carriers]);
}
